import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { ScheduleItem, Appointment, Task } from '../components/SchedulerTypes';
import { 
  getDoctors, 
  getStaff, 
  getAllStaffForTasks,
  getRooms, 
  getScheduleItems,
  getMonthlySchedulePreview,
  getMonthlyScheduleSmart,
  getMonthlyScheduleComplete,
  getScheduleItemsForDay, 
  createScheduleItem, 
  updateScheduleItem,
  deleteScheduleItem,
  deleteScheduleItems,
  Doctor as DBDoctor,
  Staff as DBStaff, 
  StaffForTasks,
  Room as DBRoom, 
  ScheduleItem as DBScheduleItem,
  DentalProcedure as DBDentalProcedure, 
  AdminTask as DBAdminTask 
} from '../services/adminService';
import {
  getStaffTodos,
  createStaffTodo,
  updateStaffTodo,
  deleteStaffTodo,
  StaffTodo
} from '../services/staffTodoService';
import { formatDateToString } from '../lib/dateUtils';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { Reassignment } from '@/components/ConflictResolutionModal';
import { applyReassignments } from '@/services/conflictDetectionService';
// Note: Using empty arrays for appointments and tasks since live data integration is pending

// Type mappings for compatibility
interface Doctor {
  id: string;
  name: string;
  short_name?: string;
  specialization: string;
  color: string;
  status: 'active' | 'inactive';
  company?: string;
  priority: number;
  join_date?: string;
  preferred_room_id?: string;
  created_at: string;
  updated_at: string;
}

interface Staff {
  id: string;
  name: string;
  short_name?: string;
  role: string;
  color: string;
  status: 'active' | 'inactive';
  priority: number;
  created_at: string;
  updated_at: string;
}

interface Room {
  id: string;
  name: string;
  clinic: string;
  clinic_id?: string;
  color?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface DentalProcedure {
  id: string;
  name: string;
  description: string;
  duration: number;
  estimatedDuration: number;
  category: string;
  price: number;
  status: 'active' | 'inactive';
}

interface AdminTaskType {
  id: string;
  name: string;
  description: string;
  duration: number;
  estimatedDuration: number;
  category: string;
  priority: 'low' | 'medium' | 'high';
  status: 'active' | 'inactive';
}

interface UseSchedulerState {
  doctors: Doctor[];
  staff: Staff[];
  allStaffForTasks: StaffForTasks[];
  rooms: Room[];
  scheduleItems: ScheduleItem[];
  appointments: Appointment[];
  tasks: Task[];
  procedures: DentalProcedure[];
  adminTasks: AdminTaskType[];
  addScheduleItem: (item: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'>, reassignments?: Reassignment[]) => void;
  updateScheduleItem: (id: string, updates: Partial<ScheduleItem>) => void;
  deleteScheduleItem: (id: string) => void;
  deleteScheduleSeries: (ids: string[]) => void;
  addAppointment: (appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateAppointment: (id: string, updates: Partial<Appointment>) => void;
  deleteAppointment: (id: string) => void;
  addTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateTask: (id: string, updates: Partial<Task>) => void;
  deleteTask: (id: string) => void;
  updateDoctor: (id: string, updates: Partial<Doctor>) => void;
  updateStaff: (id: string, updates: Partial<Staff>) => void;
  updateProcedure: (id: string, updates: Partial<DentalProcedure>) => void;
  updateAdminTask: (id: string, updates: Partial<AdminTaskType>) => void;
  addDoctor: (doctor: Omit<Doctor, 'id'>) => void;
  addStaff: (staff: Omit<Staff, 'id'>) => void;
  addProcedure: (procedure: Omit<DentalProcedure, 'id'>) => void;
  addAdminTask: (task: Omit<AdminTaskType, 'id'>) => void;
  getAppointmentForScheduleItem: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem: (scheduleItemId: string) => Task | undefined;
  // Staff todos
  staffTodos: StaffTodo[];
  addStaffTodo: (todo: Omit<StaffTodo, 'id' | 'created_at' | 'updated_at'>) => void;
  updateStaffTodo: (todo: StaffTodo) => void;
  deleteStaffTodo: (id: string) => void;
  // Loading states
  isCreatingScheduleItem: boolean;
  isUpdatingScheduleItem: boolean;
  isDeletingScheduleItem: boolean;
  isLoadingSchedules: boolean;
  isFetchingSchedules: boolean;
  // Lazy loading for monthly view
  loadFullDaySchedule: (date: string) => Promise<ScheduleItem[]>;
  fullyLoadedDays: Set<string>;
  dayScheduleCache: Record<string, ScheduleItem[]>;
  loadingDays: Set<string>;
  // Refresh function for external triggers (e.g., Plato sync)
  refreshScheduleItems: () => void;
}

export const useSchedulerState = (
  currentView?: 'daily' | 'weekly' | 'monthly' | 'report' | 'tasks' | 'admin' | 'patients' | 'inventory',
  currentDate?: Date
): UseSchedulerState => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentClinic } = useClinic();
  const { user } = useAuth();
  const clinicId = currentClinic?.id;
  const isShellOnlyView = currentView === 'patients' || currentView === 'inventory';
  
  // Track loaded date ranges per view to avoid redundant fetches
  const [loadedRanges, setLoadedRanges] = useState<Record<string, Array<{start: string, end: string}>>>({});

  // Latest-value ref so async mutation callbacks (onSuccess/onError/onSettled, which can fire
  // after the user has navigated and loaded new ranges) always see the current ranges instead
  // of the snapshot captured when the mutation started.
  const loadedRangesRef = React.useRef(loadedRanges);
  loadedRangesRef.current = loadedRanges;

  // Track fully loaded days for monthly view lazy loading
  const [fullyLoadedDays, setFullyLoadedDays] = useState<Set<string>>(new Set());
  const [dayScheduleCache, setDayScheduleCache] = useState<Record<string, ScheduleItem[]>>({});
  const [loadingDays, setLoadingDays] = useState<Set<string>>(new Set());
  
  // Reset loaded ranges when clinic changes
  React.useEffect(() => {
    setLoadedRanges({});
    setFullyLoadedDays(new Set());
    setDayScheduleCache({});
    setLoadingDays(new Set());
    console.log('[useSchedulerState] Clinic changed, resetting loaded ranges');
  }, [clinicId]);
  
  // Track the previous month to detect month changes
  const prevMonthKeyRef = React.useRef<string>();
  
  // Reset loaded ranges for monthly view when month changes
  React.useEffect(() => {
    if (currentView === 'monthly' && currentDate) {
      const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
      
      if (prevMonthKeyRef.current && prevMonthKeyRef.current !== monthKey) {
        console.log('[useSchedulerState] Month changed from', prevMonthKeyRef.current, 'to', monthKey, ', clearing monthly cache');
        setLoadedRanges(prev => ({
          ...prev,
          monthly: [] // Clear only monthly view cache
        }));
        setFullyLoadedDays(new Set());
        setDayScheduleCache({});
        // Force a re-fetch for the new month
        queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId] });
      }
      
      prevMonthKeyRef.current = monthKey;
    }
  }, [currentView, currentDate, clinicId, queryClient]);

  // Helper function to check if a date is within loaded ranges for the current view
  const isDateInLoadedRanges = useCallback((date: string, view: string) => {
    const viewRanges = loadedRangesRef.current[view] || [];
    return viewRanges.some(range => date >= range.start && date <= range.end);
  }, [loadedRanges]);

  // Helper function to calculate the optimal fetch range
  const calculateFetchRange = useCallback((targetDate: Date, view: string) => {
    let startDate: Date;
    let endDate: Date;
    
    switch (view) {
      case 'daily':
        // For daily view: fetch current day + 7 days before/after (15 days total)
        startDate = new Date(targetDate);
        startDate.setDate(startDate.getDate() - 7);
        endDate = new Date(targetDate);
        endDate.setDate(endDate.getDate() + 7);
        break;
        
      case 'weekly':
        // For weekly view: fetch current week + 1 week before/after
        startDate = new Date(targetDate);
        startDate.setDate(startDate.getDate() - startDate.getDay()); // Start of week
        startDate.setDate(startDate.getDate() - 7); // One week before
        endDate = new Date(targetDate);
        endDate.setDate(endDate.getDate() - endDate.getDay() + 6); // End of week
        endDate.setDate(endDate.getDate() + 7); // One week after
        break;
        
      case 'monthly':
        // For monthly view: fetch only the current month
        startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        endDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
        break;
        
      default:
        return { start: undefined, end: undefined };
    }
    
    return {
      start: formatDateToString(startDate),
      end: formatDateToString(endDate)
    };
  }, []);

  // Calculate date range based on view and what's already loaded
  const { startDate, endDate, shouldFetch } = React.useMemo(() => {
    if (!currentDate || !currentView) {
      // Use default range if no view/date provided
      return { startDate: undefined, endDate: undefined, shouldFetch: true };
    }

    const dateStr = formatDateToString(currentDate);

    // For report/admin views, always use default range
    if (['report', 'tasks', 'admin', 'patients', 'inventory'].includes(currentView)) {
      return { startDate: undefined, endDate: undefined, shouldFetch: true };
    }
    
    // Check if current date is already in loaded ranges for this specific view
    if (isDateInLoadedRanges(dateStr, currentView)) {
      console.log('[useSchedulerState] Date already in loaded range for view:', currentView, ', skipping fetch');
      // Return the widest loaded range that contains this date for this view
      const viewRanges = loadedRanges[currentView] || [];
      const relevantRanges = viewRanges.filter(range => 
        dateStr >= range.start && dateStr <= range.end
      );
      if (relevantRanges.length > 0) {
        // Find the widest range
        const widestRange = relevantRanges.reduce((prev, curr) => {
          const prevSpan = new Date(prev.end).getTime() - new Date(prev.start).getTime();
          const currSpan = new Date(curr.end).getTime() - new Date(curr.start).getTime();
          return currSpan > prevSpan ? curr : prev;
        });
        return {
          startDate: widestRange.start,
          endDate: widestRange.end,
          shouldFetch: false
        };
      }
    }
    
    // Calculate new fetch range
    const { start, end } = calculateFetchRange(currentDate, currentView);
    
    console.log('[useSchedulerState] Calculating new fetch range:', {
      view: currentView,
      currentDate: dateStr,
      startDate: start,
      endDate: end,
      loadedRanges
    });
    
    return {
      startDate: start,
      endDate: end,
      shouldFetch: true
    };
  }, [currentDate, currentView, loadedRanges, isDateInLoadedRanges, calculateFetchRange]);
  
  // Prefetch adjacent ranges when user is near the edge
  React.useEffect(() => {
    if (!currentDate || !currentView || !shouldFetch) return;
    
    const dateStr = formatDateToString(currentDate);

    // Check if we're near the edge of any loaded range for the current view
    const viewRanges = loadedRanges[currentView] || [];
    viewRanges.forEach(range => {
      const startDate = new Date(range.start);
      const endDate = new Date(range.end);
      const currentDateObj = new Date(dateStr);
      
      // Calculate days from edges
      const daysFromStart = Math.floor((currentDateObj.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysFromEnd = Math.floor((endDate.getTime() - currentDateObj.getTime()) / (1000 * 60 * 60 * 24));
      
      // Prefetch if within 3 days of edge
      if (daysFromStart <= 3 && daysFromStart >= 0) {
        console.log('[useSchedulerState] Near start edge, consider prefetching earlier dates');
        // TODO: Implement prefetch for earlier dates
      }
      
      if (daysFromEnd <= 3 && daysFromEnd >= 0) {
        console.log('[useSchedulerState] Near end edge, consider prefetching later dates');
        // TODO: Implement prefetch for later dates
      }
    });
  }, [currentDate, currentView, shouldFetch, loadedRanges]);

  // Fetch data from Supabase
  const doctorsQuery = useQuery({
    queryKey: ['doctors', clinicId],
    queryFn: () => getDoctors(clinicId),
    enabled: !!clinicId && !isShellOnlyView,
  });

  const staffQuery = useQuery({
    queryKey: ['staff', clinicId],
    queryFn: () => getStaff(clinicId),
    enabled: !!clinicId && !isShellOnlyView,
  });

  // Query for all staff across clinics (for admin users in task assignment)
  const allStaffQuery = useQuery({
    queryKey: ['allStaffForTasks'],
    queryFn: () => getAllStaffForTasks(),
    enabled: user?.role === 'admin' && !isShellOnlyView,
  });

  const roomsQuery = useQuery({
    queryKey: ['rooms', clinicId],
    queryFn: () => getRooms(clinicId),
    enabled: !!clinicId && !isShellOnlyView,
  });

  const scheduleItemsQuery = useQuery({
    queryKey: ['scheduleItems', clinicId, startDate, endDate, currentView],
    queryFn: async () => {
      console.log('[useSchedulerState] Query starting for view:', currentView, 'clinic:', clinicId);
      console.log('[useSchedulerState] Date range:', startDate, 'to', endDate, 'shouldFetch:', shouldFetch);
      
      // If we shouldn't fetch, check if we have data in cache for this specific range
      if (!shouldFetch) {
        console.log('[useSchedulerState] Skipping fetch, checking for existing cached data');
        const existingData = queryClient.getQueryData<ScheduleItem[]>(['scheduleItems', clinicId, startDate, endDate, currentView]);
        if (existingData) {
          console.log('[useSchedulerState] Found existing cached data:', existingData.length, 'items');
          return existingData;
        }
        // If no existing data, allow fetch to proceed
        console.log('[useSchedulerState] No cached data found, proceeding with fetch');
      }
      
      // For monthly view, use complete loading with no limits
      if (currentView === 'monthly' && clinicId && startDate && endDate) {
        console.log('[useSchedulerState] Using complete monthly loading for clinic:', clinicId);
        try {
          const data = await getMonthlyScheduleComplete(clinicId, startDate, endDate);
          console.log('[useSchedulerState] Complete monthly fetch returned:', data?.length || 0, 'items');
          console.log('[useSchedulerState] All items loaded successfully without limits');
          return data;
        } catch (error) {
          console.error('[useSchedulerState] Complete monthly loading failed:', error);
          // Fall back to smart loading if complete loading fails
          try {
            console.log('[useSchedulerState] Falling back to smart loading');
            const fallbackData = await getMonthlyScheduleSmart(clinicId, startDate, endDate, 8);
            return fallbackData || [];
          } catch (fallbackError) {
            console.error('[useSchedulerState] Fallback also failed:', fallbackError);
            return [];
          }
        }
      }
      
      // Use regular fetch for other views
      const data = await getScheduleItems(clinicId, startDate, endDate, currentView);
      console.log('[useSchedulerState] Regular fetch returned:', data?.length || 0, 'items');
      
      // Update loaded ranges when data is successfully fetched
      if (startDate && endDate && shouldFetch && currentView) {
        setLoadedRanges(prev => {
          const viewRanges = prev[currentView] || [];
          // Check if this range is already tracked for this view
          const isAlreadyTracked = viewRanges.some(range => 
            range.start === startDate && range.end === endDate
          );
          
          if (!isAlreadyTracked) {
            console.log('[useSchedulerState] Adding new loaded range for view:', currentView, { start: startDate, end: endDate });
            return {
              ...prev,
              [currentView]: [...viewRanges, { start: startDate, end: endDate }]
            };
          }
          
          return prev;
        });
      }
      
      return data;
    },
    enabled: !!clinicId && !isShellOnlyView, // Patients/inventory shell routes do not need scheduler data
    staleTime: shouldFetch ? 0 : 5 * 60 * 1000, // Fresh data when fetching, stale when cached
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (updated from deprecated cacheTime)
    placeholderData: (previousData) => previousData // Keep previous data while fetching new range
  });

  const staffTodosQuery = useQuery({
    queryKey: ['staffTodos', user?.id],
    queryFn: () => getStaffTodos(undefined, user),
    enabled: !isShellOnlyView, // Patients/inventory shell routes do not need task data
  });

  // Mutations for schedule items
  const createScheduleItemMutation = useMutation({
    mutationFn: createScheduleItem,
    onMutate: async (newItem) => {
      console.log('[createScheduleItemMutation.onMutate] Starting optimistic create for new item');
      
      // Cancel any outgoing refetches for all loaded ranges
      await queryClient.cancelQueries({ queryKey: ['scheduleItems', clinicId] });

      // Snapshot the previous values for all loaded ranges
      const previousData: Record<string, any> = {};
      
      // Create a temporary item with a temporary ID
      const tempItem = {
        id: `temp-${Date.now()}`,
        doctorId: newItem.doctorId,
        staffId: newItem.staffId,
        roomId: newItem.roomId,
        startTime: newItem.startTime,
        endTime: newItem.endTime,
        date: newItem.date,
        endDate: newItem.endDate,
        type: newItem.type,
        title: newItem.title,
        notes: newItem.notes,
        procedure: newItem.procedure,
        adminTask: newItem.adminTask,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        clinic_id: newItem.clinic_id || clinicId,
      };

      // Update all loaded ranges optimistically
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          const queryKey = ['scheduleItems', clinicId, range.start, range.end, view];
          const currentData = queryClient.getQueryData(queryKey);
          
          // Store previous data for rollback
          previousData[`${view}-${range.start}-${range.end}`] = currentData;
          
          // Check if the new item's date falls within this range
          const itemDate = newItem.date;
          if (itemDate >= range.start && itemDate <= range.end) {
            // Optimistically add the item to this cache
            queryClient.setQueryData(queryKey, (old: any) => {
              console.log('[createScheduleItemMutation.onMutate] Adding item to range:', range.start, 'to', range.end, 'view:', view);
              if (!old) return [tempItem];
              return [...old, tempItem];
            });
          }
        });
      });

      // Return a context object with the snapshots
      return { previousData, tempItem };
    },
    onSuccess: (data) => {
      console.log('[createScheduleItemMutation.onSuccess] Schedule item created, response:', data);
      
      // Update all loaded ranges that should contain this item
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          const queryKey = ['scheduleItems', clinicId, range.start, range.end, view];
          
          // Check if the new item's date falls within this range
          const itemDate = data.date;
          if (itemDate >= range.start && itemDate <= range.end) {
            queryClient.setQueryData(queryKey, (old: any) => {
              if (!old) return [data];
              
              // Check if the item already exists (replace temp item)
              const existingIndex = old.findIndex((item: any) => 
                item.id === data.id || item.id.startsWith('temp-')
              );
              
              if (existingIndex >= 0) {
                const newItems = [...old];
                newItems[existingIndex] = data;
                return newItems;
              }
              
              // Add to the end if not found
              return [...old, data];
            });
          }
        });
      });
      
      // Invalidate all loaded ranges to ensure consistency
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      
      // Also invalidate the current active query to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, startDate, endDate, currentView] });
      
      toast({
        title: "Success",
        description: "Schedule item created successfully",
      });
    },
    onError: (error: any, newItem, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        Object.keys(loadedRangesRef.current).forEach(view => {
          const viewRanges = loadedRangesRef.current[view] || [];
          viewRanges.forEach(range => {
            const key = `${view}-${range.start}-${range.end}`;
            if (context.previousData[key]) {
              queryClient.setQueryData(
                ['scheduleItems', clinicId, range.start, range.end, view],
                context.previousData[key]
              );
            }
          });
        });
      }
      
      console.error('Error creating schedule item:', error);
      const errorMessage = error?.message || error?.error || "Failed to create schedule item";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency (immediate, no delay)
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      // Ensure current query is also invalidated
      queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, startDate, endDate, currentView] });
    },
  });

  const updateScheduleItemMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ScheduleItem> }) => 
      updateScheduleItem(id, updates),
    onMutate: async ({ id, updates }) => {
      const baseId = id.split('_day_')[0];
      console.log('[updateScheduleItemMutation.onMutate] Starting optimistic update for:', id, 'baseId:', baseId);
      
      // Cancel any outgoing refetches for all loaded ranges
      await queryClient.cancelQueries({ queryKey: ['scheduleItems', clinicId] });

      // Snapshot the previous values for all loaded ranges
      const previousData: Record<string, any> = {};
      
      // Update all loaded ranges optimistically
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          const queryKey = ['scheduleItems', clinicId, range.start, range.end, view];
          const currentData = queryClient.getQueryData(queryKey);
          
          // Store previous data for rollback
          previousData[`${view}-${range.start}-${range.end}`] = currentData;
          
          // Optimistically update the item in cache
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return [];
            
            const hasDateChange = Object.prototype.hasOwnProperty.call(updates, 'date') || Object.prototype.hasOwnProperty.call(updates, 'endDate');
            
            if (!hasDateChange) {
              // Simple in-place merge
              return old.map((item: ScheduleItem) => {
                if (item.id === baseId || item.id === id) {
                  console.log('[updateScheduleItemMutation.onMutate] Updating item:', item.id, 'with:', updates, 'in view:', view);
                  return { ...item, ...updates };
                }
                return item;
              });
            }
            
            // If date/endDate changed, compute overlap with this cached range.
            const [rangeStart, rangeEnd] = [range.start, range.end];
            const updatedList: ScheduleItem[] = [];
            
            for (const item of old as ScheduleItem[]) {
              if (item.id !== baseId && item.id !== id) {
                updatedList.push(item);
                continue;
              }
              // Determine new start/end in YYYY-MM-DD
              const newStart = (updates.date as string) ?? item.date;
              const newEnd = (updates.endDate as string) ?? (item.endDate || item.date);
              const overlaps = !(newEnd < rangeStart || newStart > rangeEnd);
              if (!overlaps) {
                console.log('[updateScheduleItemMutation.onMutate] Removing item from non-overlapping range', { view, rangeStart, rangeEnd, newStart, newEnd });
                // Skip adding the item to this range's cache
                continue;
              }
              // Overlaps: keep with merged updates
              updatedList.push({ ...item, ...updates });
            }
            return updatedList;
          });
        });
      });

      // Return a context object with the snapshots
      return { previousData, id: baseId };
    },
    onSuccess: () => {
      // Invalidate all loaded ranges to ensure consistency
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      
      toast({
        title: "Success",
        description: "Schedule item updated successfully",
      });
    },
    onError: (error: any, { id }, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        Object.keys(loadedRangesRef.current).forEach(view => {
          const viewRanges = loadedRangesRef.current[view] || [];
          viewRanges.forEach(range => {
            const key = `${view}-${range.start}-${range.end}`;
            if (context.previousData[key]) {
              queryClient.setQueryData(
                ['scheduleItems', clinicId, range.start, range.end, view],
                context.previousData[key]
              );
            }
          });
        });
      }
      
      console.error('Error updating schedule item:', error);
      const errorMessage = error?.message || error?.error || "Failed to update schedule item";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency (immediate, no delay)
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      // Ensure current query is also invalidated
      queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, startDate, endDate, currentView] });
    },
  });

  const deleteScheduleItemMutation = useMutation({
    mutationFn: deleteScheduleItem,
    onMutate: async (itemId: string) => {
      console.log('[deleteScheduleItemMutation.onMutate] Starting optimistic delete for:', itemId);
      
      // Cancel any outgoing refetches for all loaded ranges
      await queryClient.cancelQueries({ queryKey: ['scheduleItems', clinicId] });

      // Snapshot the previous values for all loaded ranges
      const previousData: Record<string, any> = {};
      
      // Update all loaded ranges optimistically
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          const queryKey = ['scheduleItems', clinicId, range.start, range.end, view];
          const currentData = queryClient.getQueryData(queryKey);
          
          // Store previous data for rollback
          previousData[`${view}-${range.start}-${range.end}`] = currentData;
          
          // Optimistically remove the item from this cache
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return [];
            const filtered = old.filter((item: ScheduleItem) => item.id !== itemId);
            console.log('[deleteScheduleItemMutation.onMutate] Removing item from range:', range.start, 'to', range.end, 'view:', view);
            return filtered;
          });
        });
      });

      // Return a context object with the snapshot values
      return { previousData };
    },
    onSuccess: () => {
      // Invalidate all loaded ranges to ensure consistency
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      
      toast({
        title: "Success",
        description: "Schedule item deleted successfully",
      });
    },
    onError: (error: any, itemId, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        Object.keys(loadedRangesRef.current).forEach(view => {
          const viewRanges = loadedRangesRef.current[view] || [];
          viewRanges.forEach(range => {
            const key = `${view}-${range.start}-${range.end}`;
            if (context.previousData[key]) {
              queryClient.setQueryData(
                ['scheduleItems', clinicId, range.start, range.end, view],
                context.previousData[key]
              );
            }
          });
        });
      }
      
      console.error('Error deleting schedule item:', error);
      const errorMessage = error?.message || error?.error || "Failed to delete schedule item";
      toast({
        title: "Error", 
        description: errorMessage,
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always refetch after error or success (immediate, no delay)
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      // Ensure current query is also invalidated
      queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, startDate, endDate, currentView] });
    },
  });

  // Bulk delete: remove every row of a multi-day leave in one request.
  const deleteScheduleSeriesMutation = useMutation({
    mutationFn: deleteScheduleItems,
    onMutate: async (itemIds: string[]) => {
      const idSet = new Set(itemIds.map((id) => id.split('_day_')[0]));
      await queryClient.cancelQueries({ queryKey: ['scheduleItems', clinicId] });

      const previousData: Record<string, any> = {};
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          const queryKey = ['scheduleItems', clinicId, range.start, range.end, view];
          const currentData = queryClient.getQueryData(queryKey);
          previousData[`${view}-${range.start}-${range.end}`] = currentData;
          queryClient.setQueryData(queryKey, (old: any) => {
            if (!old) return [];
            return old.filter((item: ScheduleItem) => !idSet.has(item.id.split('_day_')[0]));
          });
        });
      });
      return { previousData };
    },
    onSuccess: (_data, itemIds) => {
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      const count = new Set(itemIds.map((id) => id.split('_day_')[0])).size;
      toast({
        title: "Leave deleted",
        description: `Removed all ${count} day${count === 1 ? '' : 's'} of the leave.`,
      });
    },
    onError: (error: any, _itemIds, context) => {
      if (context?.previousData) {
        Object.keys(loadedRangesRef.current).forEach(view => {
          const viewRanges = loadedRangesRef.current[view] || [];
          viewRanges.forEach(range => {
            const key = `${view}-${range.start}-${range.end}`;
            if (context.previousData[key]) {
              queryClient.setQueryData(
                ['scheduleItems', clinicId, range.start, range.end, view],
                context.previousData[key]
              );
            }
          });
        });
      }
      console.error('Error deleting leave series:', error);
      const errorMessage = error?.message || error?.error || "Failed to delete leave";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    },
    onSettled: () => {
      Object.keys(loadedRangesRef.current).forEach(view => {
        const viewRanges = loadedRangesRef.current[view] || [];
        viewRanges.forEach(range => {
          queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, range.start, range.end, view] });
        });
      });
      queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId, startDate, endDate, currentView] });
    },
  });

  // Mutations for staff todos
  const createStaffTodoMutation = useMutation({
    mutationFn: createStaffTodo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffTodos', user?.id] });
      toast({
        title: "Success",
        description: "Task created successfully",
      });
    },
    onError: (error) => {
      console.error('Error creating staff todo:', error);
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive",
      });
    },
  });

  const updateStaffTodoMutation = useMutation({
    mutationFn: async ({ id, updates, oldTodo }: { id: string; updates: Partial<StaffTodo>; oldTodo?: StaffTodo }) => {
      // Check if status changed and log it
      if (oldTodo && updates.status && oldTodo.status !== updates.status) {
        try {
          // Import the service at the top of the function
          const { taskHistoryService } = await import('@/services/taskHistoryService');
          await taskHistoryService.logStatusChange(id, oldTodo.status, updates.status);
        } catch (error) {
          console.error('Failed to log status change:', error);
          // Continue with the update even if logging fails
        }
      }
      
      return updateStaffTodo(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffTodos', user?.id] });
      toast({
        title: "Success",
        description: "Task updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating staff todo:', error);
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive",
      });
    },
  });

  const deleteStaffTodoMutation = useMutation({
    mutationFn: deleteStaffTodo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffTodos', user?.id] });
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    },
    onError: (error) => {
      console.error('Error deleting staff todo:', error);
      toast({
        title: "Error",
        description: "Failed to delete task",
        variant: "destructive",
      });
    },
  });

  // Using empty arrays for appointments and tasks since live data integration is pending
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [procedures, setProcedures] = useState<DentalProcedure[]>([]);
  const [adminTasks, setAdminTasks] = useState<AdminTaskType[]>([]);

  // Convert Supabase format to existing interface format with proper type mapping
  const doctors: Doctor[] = (doctorsQuery.data || []).map(doctor => ({
    id: doctor.id,
    name: doctor.name,
    short_name: doctor.short_name,
    specialization: doctor.specialization,
    color: doctor.color,
    status: doctor.status,
    company: doctor.company,
    priority: doctor.priority,
    join_date: doctor.join_date,
    preferred_room_id: doctor.preferred_room_id,
    created_at: doctor.created_at,
    updated_at: doctor.updated_at,
  }));

  const staff: Staff[] = (staffQuery.data || []).map(staffMember => ({
    id: staffMember.id,
    name: staffMember.name,
    short_name: staffMember.short_name,
    role: staffMember.role,
    color: staffMember.color,
    status: staffMember.status,
    priority: staffMember.priority,
    created_at: staffMember.created_at,
    updated_at: staffMember.updated_at,
  }));

  const rooms: Room[] = (roomsQuery.data || []).map(room => ({
    id: room.id,
    name: room.name,
    clinic: room.clinic,
    clinic_id: room.clinic_id,
    color: room.color,
    status: room.status,
    created_at: room.created_at,
    updated_at: room.updated_at,
  }));

  // Merge schedule items from all loaded ranges for the current view
  const scheduleItems: ScheduleItem[] = React.useMemo(() => {
    // Always try to merge from all cached ranges for the current view to get the most complete dataset
    const allCachedItems: ScheduleItem[] = [];
    const addedIds = new Set<string>();
    
    // Get all cached schedule items for this clinic and view
    const viewRanges = loadedRanges[currentView || ''] || [];
    let hasCachedData = false;
    
    viewRanges.forEach(range => {
      const cachedData = queryClient.getQueryData<ScheduleItem[]>([
        'scheduleItems', 
        clinicId, 
        range.start, 
        range.end,
        currentView
      ]);
      
      if (cachedData && cachedData.length > 0) {
        hasCachedData = true;
        cachedData.forEach(item => {
          if (!addedIds.has(item.id)) {
            addedIds.add(item.id);
            allCachedItems.push(item);
          }
        });
      }
    });
    
    // If we have cached data from multiple ranges, use that for a more complete view
    if (hasCachedData && viewRanges.length > 0) {
      console.log('[useSchedulerState] Using merged cached data for view:', currentView, {
        totalItems: allCachedItems.length,
        ranges: viewRanges.length,
        shouldFetch,
        hasCurrentQuery: !!scheduleItemsQuery.data
      });
      return allCachedItems;
    }
    
    // Fallback to current query data if no cached ranges
    return scheduleItemsQuery.data || [];
  }, [scheduleItemsQuery.data, shouldFetch, loadedRanges, currentView, clinicId, queryClient]);

  const staffTodos: StaffTodo[] = staffTodosQuery.data || [];

  const addScheduleItem = useCallback((item: Omit<ScheduleItem, 'id'>, reassignments?: Reassignment[]) => {
    console.log('[addScheduleItem] Called with item and reassignments:', { item, reassignments });
    
    if (!clinicId) {
      console.error('[addScheduleItem] No clinic selected');
      toast({
        title: "No Clinic Selected",
        description: "Please select a specific clinic before creating schedules. You cannot create schedules in the 'All Clinics' view.",
        variant: "destructive",
      });
      return;
    }
    
    // Remove the clinic_id from item if it exists to avoid conflicts
    const { clinic_id: _, ...itemWithoutClinicId } = item as any;
    
    const itemWithTimestamps = {
      ...itemWithoutClinicId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      clinic_id: clinicId,
    };
    
    console.log('[addScheduleItem] Mutating with data:', itemWithTimestamps);
    
    // Create the main schedule item (leave/MC)
    createScheduleItemMutation.mutate(itemWithTimestamps);
    
    // If there are reassignments, apply them
    if (reassignments && reassignments.length > 0) {
      console.log('[addScheduleItem] Applying reassignments:', reassignments);
      
      // Apply each reassignment by updating the affected schedule items
      reassignments.forEach(reassignment => {
        updateScheduleItemMutation.mutate({
          id: reassignment.scheduleId,
          updates: {
            staffId: reassignment.newStaffId,
            notes: `Auto-reassigned due to ${item.staffId} on ${item.type}. Previous DA preference rank: ${reassignment.preferenceRank}`
          }
        });
      });
      
      toast({
        title: "Schedule Created with Reassignments",
        description: `${item.type === 'mc' ? 'Urgent leave' : 'Leave'} scheduled and ${reassignments.length} schedule(s) reassigned to backup DAs.`,
      });
    }
  }, [createScheduleItemMutation, updateScheduleItemMutation, clinicId, toast]);

  const updateScheduleItemCallback = useCallback((id: string, updates: Partial<ScheduleItem>) => {
    updateScheduleItemMutation.mutate({ id, updates });
  }, [updateScheduleItemMutation]);

  const deleteScheduleItemCallback = useCallback((id: string) => {
    console.log('[deleteScheduleItemCallback] Deleting item with id:', id);
    deleteScheduleItemMutation.mutate(id);
    setAppointments(prevAppointments => prevAppointments.filter(appointment => appointment.scheduleItemId !== id));
    setTasks(prevTasks => prevTasks.filter(task => task.scheduleItemId !== id));
  }, [deleteScheduleItemMutation]);

  const deleteScheduleSeriesCallback = useCallback((ids: string[]) => {
    console.log('[deleteScheduleSeriesCallback] Deleting leave series:', ids);
    deleteScheduleSeriesMutation.mutate(ids);
    const baseIds = new Set(ids.map((id) => id.split('_day_')[0]));
    setAppointments(prev => prev.filter(a => !baseIds.has((a.scheduleItemId || '').split('_day_')[0])));
    setTasks(prev => prev.filter(t => !baseIds.has((t.scheduleItemId || '').split('_day_')[0])));
  }, [deleteScheduleSeriesMutation]);

  const addAppointment = useCallback((appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newAppointment: Appointment = {
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...appointment,
    };
    setAppointments(prevAppointments => [...prevAppointments, newAppointment]);
  }, []);

  const updateAppointment = useCallback((id: string, updates: Partial<Appointment>) => {
    setAppointments(prevAppointments =>
      prevAppointments.map(appointment =>
        appointment.id === id ? { ...appointment, ...updates, updatedAt: new Date().toISOString() } : appointment
      )
    );
  }, []);

  const deleteAppointment = useCallback((id: string) => {
    setAppointments(prevAppointments => prevAppointments.filter(appointment => appointment.id !== id));
  }, []);

  const addTask = useCallback((task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newTask: Task = {
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...task,
    };
    setTasks(prevTasks => [...prevTasks, newTask]);
  }, []);

  const updateTask = useCallback((id: string, updates: Partial<Task>) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === id ? { ...task, ...updates, updatedAt: new Date().toISOString() } : task
      )
    );
  }, []);

  const deleteTask = useCallback((id: string) => {
    setTasks(prevTasks => prevTasks.filter(task => task.id !== id));
  }, []);

  // TODO: Implement these functions when admin features are needed
  const updateDoctor = useCallback((id: string, updates: Partial<Doctor>) => {
    throw new Error('Doctor update functionality not yet implemented');
  }, []);

  const updateStaff = useCallback((id: string, updates: Partial<Staff>) => {
    throw new Error('Staff update functionality not yet implemented');
  }, []);

  const updateProcedure = useCallback((id: string, updates: Partial<DentalProcedure>) => {
    throw new Error('Procedure update functionality not yet implemented');
  }, []);

  const updateAdminTask = useCallback((id: string, updates: Partial<AdminTaskType>) => {
    throw new Error('Admin task update functionality not yet implemented');
  }, []);

  const addDoctor = useCallback((doctor: Omit<Doctor, 'id'>) => {
    throw new Error('Doctor creation functionality not yet implemented');
  }, []);

  const addStaff = useCallback((staff: Omit<Staff, 'id'>) => {
    throw new Error('Staff creation functionality not yet implemented');
  }, []);

  const addProcedure = useCallback((procedure: Omit<DentalProcedure, 'id'>) => {
    throw new Error('Procedure creation functionality not yet implemented');
  }, []);

  const addAdminTask = useCallback((task: Omit<AdminTaskType, 'id'>) => {
    throw new Error('Admin task creation functionality not yet implemented');
  }, []);

  const getAppointmentForScheduleItem = useCallback((scheduleItemId: string) => {
    return appointments.find(appointment => appointment.scheduleItemId === scheduleItemId);
  }, [appointments]);

  const getTaskForScheduleItem = useCallback((scheduleItemId: string) => {
    return tasks.find(task => task.scheduleItemId === scheduleItemId);
  }, [tasks]);

  const addStaffTodo = useCallback((todo: Omit<StaffTodo, 'id' | 'created_at' | 'updated_at'>) => {
    if (!clinicId) {
      toast({
        title: "Error",
        description: "No clinic selected",
        variant: "destructive",
      });
      return;
    }
    createStaffTodoMutation.mutate({
      ...todo,
      clinic_id: clinicId,
    });
  }, [createStaffTodoMutation, clinicId, toast]);

  const updateStaffTodoCallback = useCallback((todo: StaffTodo) => {
    // Get the old todo from cache to check if status changed
    const oldTodo = staffTodos.find(t => t.id === todo.id);
    
    // Extract only the actual database columns, excluding joined data like 'project'
    const { project, ...todoColumns } = todo as any;
    
    updateStaffTodoMutation.mutate({ id: todo.id, updates: todoColumns, oldTodo });
  }, [updateStaffTodoMutation, staffTodos]);

  const deleteStaffTodoCallback = useCallback((id: string) => {
    deleteStaffTodoMutation.mutate(id);
  }, [deleteStaffTodoMutation]);

  // Function to load full day schedule when user scrolls
  const loadFullDaySchedule = useCallback(async (date: string): Promise<ScheduleItem[]> => {
    // Check if already fully loaded
    if (fullyLoadedDays.has(date)) {
      return dayScheduleCache[date] || [];
    }

    // Check if already loading
    if (loadingDays.has(date)) {
      // Wait for existing load to complete
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!loadingDays.has(date)) {
            clearInterval(checkInterval);
            resolve(dayScheduleCache[date] || []);
          }
        }, 100);
      });
    }

    if (!clinicId) {
      console.error('[loadFullDaySchedule] No clinic selected');
      return [];
    }

    // Mark as loading
    setLoadingDays(prev => new Set([...prev, date]));

    try {
      console.log('[loadFullDaySchedule] Loading full schedule for date:', date);
      const fullDayData = await getScheduleItemsForDay(clinicId, date);
      
      // Update cache
      setDayScheduleCache(prev => ({ ...prev, [date]: fullDayData }));
      setFullyLoadedDays(prev => new Set([...prev, date]));
      
      // Also update the main query cache to include these items
      queryClient.setQueryData(
        ['scheduleItems', clinicId, startDate, endDate, currentView],
        (oldData: ScheduleItem[] | undefined) => {
          if (!oldData) return fullDayData;
          
          // Replace preview items with full day data
          const otherDayItems = oldData.filter(item => item.date !== date);
          return [...otherDayItems, ...fullDayData];
        }
      );
      
      console.log('[loadFullDaySchedule] Loaded', fullDayData.length, 'items for date:', date);
      return fullDayData;
    } catch (error) {
      console.error('[loadFullDaySchedule] Error loading day schedule:', error);
      toast({
        title: "Error",
        description: "Failed to load full day schedule",
        variant: "destructive",
      });
      return [];
    } finally {
      // Remove from loading
      setLoadingDays(prev => {
        const next = new Set(prev);
        next.delete(date);
        return next;
      });
    }
  }, [clinicId, fullyLoadedDays, dayScheduleCache, loadingDays, queryClient, startDate, endDate, currentView, toast]);

  // Refresh schedule items - clears loadedRanges to force refetch
  const refreshScheduleItems = React.useCallback(() => {
    console.log('[useSchedulerState] Refreshing schedule items - clearing loadedRanges and cache');
    // Clear loaded ranges to force refetch
    setLoadedRanges({});
    // Also clear day schedule cache for monthly view
    setDayScheduleCache({});
    // Invalidate all schedule queries
    queryClient.invalidateQueries({ queryKey: ['scheduleItems', clinicId] });
  }, [clinicId, queryClient]);

  return {
    doctors,
    staff,
    allStaffForTasks: allStaffQuery.data || [],
    rooms,
    scheduleItems,
    appointments,
    tasks,
    procedures,
    adminTasks,
    addScheduleItem,
    updateScheduleItem: updateScheduleItemCallback,
    deleteScheduleItem: deleteScheduleItemCallback,
    deleteScheduleSeries: deleteScheduleSeriesCallback,
    addAppointment,
    updateAppointment,
    deleteAppointment,
    addTask,
    updateTask,
    deleteTask,
    updateDoctor,
    updateStaff,
    updateProcedure,
    updateAdminTask,
    addDoctor,
    addStaff,
    addProcedure,
    addAdminTask,
    getAppointmentForScheduleItem,
    getTaskForScheduleItem,
    // Staff todos
    staffTodos,
    addStaffTodo,
    updateStaffTodo: updateStaffTodoCallback,
    deleteStaffTodo: deleteStaffTodoCallback,
    // Loading states
    isCreatingScheduleItem: createScheduleItemMutation.isPending,
    isUpdatingScheduleItem: updateScheduleItemMutation.isPending,
    isDeletingScheduleItem: deleteScheduleItemMutation.isPending,
    isLoadingSchedules: scheduleItemsQuery.isLoading,
    isFetchingSchedules: scheduleItemsQuery.isFetching,
    // Lazy loading for monthly view
    loadFullDaySchedule,
    fullyLoadedDays,
    dayScheduleCache,
    loadingDays,
    // Refresh function for external triggers (e.g., Plato sync)
    refreshScheduleItems,
  };
};
